import React, { useEffect, useState } from 'react';
import {
  Container,
  Tabs,
  Tab,
  AppBar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Hidden,
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { KeyboardDatePicker } from '@material-ui/pickers';

import { differenceInCalendarDays, isBefore, isAfter, endOfDay, startOfDay } from 'date-fns';
import { useStoreState } from 'easy-peasy';
import usePermissions from 'utils/usePermissions';
import useStyles from './style';
import AccountsItem from './AccountsItem';
import AccountsItemMobile from './AccountsItemMobile';

export default function AdminsAccounts() {
  const style = useStyles();
  const assignedServices = useStoreState((state) => state.assignedServices);

  const [patientName, setPatientName] = useState('');
  const [skuNumber, setSkuNumber] = useState('');
  const [isInRange, setIsInRange] = useState('day');
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [servicesToView, setServicesToView] = useState([]);

  const { canViewPrices } = usePermissions();

  const handleNameChange = (e) => {
    setPatientName(e.target.value);
  };
  const handleSkuNumberChange = (e) => {
    setSkuNumber(e.target.value);
  };

  const sortedServices = () => {
    // eslint-disable-next-line
    let returnData = assignedServices.filter((service) => {
      return (
        `${service.codigo}`.includes(skuNumber) || patientName.includes(service.patient.firstName) || patientName.includes(service.patient.lastName)
      );
    });

    if (isInRange === 'day') {
      // eslint-disable-next-line
      returnData = returnData.filter((service) => {
        return differenceInCalendarDays(new Date(dateFrom), new Date(service.createdAt)) === 0;
      });
    } else {
      // eslint-disable-next-line
      returnData = returnData.filter((service) => {
        return (
          isBefore(new Date(service.createdAt), endOfDay(new Date(dateTo))) && isAfter(new Date(service.createdAt), startOfDay(new Date(dateFrom)))
        );
      });
    }

    return returnData;
  };

  useEffect(() => {
    setServicesToView(sortedServices());
  }, [dateFrom, dateTo, isInRange, patientName, skuNumber, assignedServices]);

  return (
    <Container component="main" className={style.container}>
      <AppBar className={style.appBar} position="static" color="primary">
        <Tabs
          className={style.tabs}
          value={0}
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: {
              backgroundColor: '#3f51b5',
            },
          }}
        >
          <Tab className={style.tab} label={<FormattedMessage id="patient_bills_sssigned_services" description="Assigned Services" />} />
        </Tabs>
      </AppBar>
      <Box className={style.buttonCont} display="flex" alignItems="flex-start" flexWrap="wrap">
        <Box display="flex" flexDirection="column" className={style.dateAndRangeMobile} width="230px">
          <FormattedMessage id="inventory.find_by_sku">
            {(msg) => (
              <TextField
                className={style.search}
                margin="normal"
                fullWidth
                label={<FormattedMessage id="inventory.sku_description" />}
                value={skuNumber}
                onChange={handleSkuNumberChange}
                placeholder={msg}
              />
            )}
          </FormattedMessage>
          <FormattedMessage id="inventory.find_by_patient">
            {(msg) => (
              <TextField
                className={style.search}
                margin="normal"
                fullWidth
                label={<FormattedMessage id="position.patient" />}
                value={patientName}
                onChange={handleNameChange}
                placeholder={msg}
              />
            )}
          </FormattedMessage>
        </Box>
        <Box>
          <RadioGroup
            aria-label="gender"
            name="gender1"
            value={isInRange}
            onChange={(e) => {
              setIsInRange(e.target.value);
            }}
          >
            <FormControlLabel value="day" control={<Radio />} label={<FormattedMessage id="calendar.one_day" />} />
            <FormControlLabel value="range" control={<Radio />} label={<FormattedMessage id="calendar.in_range" />} />
          </RadioGroup>
        </Box>

        <Box display="flex" flexDirection="column">
          <KeyboardDatePicker
            margin="normal"
            format="dd.MM.yyyy"
            id="time-picker"
            label={<FormattedMessage id="calendar.from" description="From" />}
            value={dateFrom}
            onChange={(date) => {
              setDateFrom(date);
            }}
            KeyboardButtonProps={{
              'aria-label': 'change time',
            }}
          />
          {isInRange === 'range' && (
            <KeyboardDatePicker
              margin="normal"
              format="dd.MM.yyyy"
              id="time-picker"
              label={<FormattedMessage id="calendar.to" description="To" />}
              value={dateTo}
              onChange={(date) => {
                setDateTo(date);
              }}
              KeyboardButtonProps={{
                'aria-label': 'change time',
              }}
            />
          )}
        </Box>
      </Box>
      <Hidden xsDown>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <FormattedMessage id="inventory.sku_description" description="Sku" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="inventory.name" description="Name" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="inventory.type" description="Type" />
              </TableCell>
              {canViewPrices && (
                <>
                  <TableCell>
                    <FormattedMessage id="inventory.price" description="Price" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="inventory.quantity" description="Quantity" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="inventory.total_cost" description="Total cost" />
                  </TableCell>
                </>
              )}
              <TableCell>
                <FormattedMessage id="position.patient" description="Patient" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="record.date" description="Date" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="inventory.status" description="Status" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servicesToView.map((item) => (
              <AccountsItem item={item} />
            ))}
          </TableBody>
        </Table>
      </Hidden>
      <Hidden smUp className={style.mobileContainer}>
        {servicesToView.map((item) => (
          <AccountsItemMobile item={item} />
        ))}
      </Hidden>
    </Container>
  );
}
